import { WaitlistAlert } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/notifications.pb';
import dayjs from 'dayjs';
import { dispatchTopBarTraySelectionEvent } from '@frontend/shared';
import { CalendarViewOffices, OfficeCalendarView, WorkingHourInfo } from '../components/calendar-view/types';
import { CALENDAR_SCROLL_TO_OFFSET_IN_MINUTES, DEFAULT_CALENDAR_SCROLL_TO_TIME } from '../constants';

const TIME_FORMAT = 'hh:mm A';

const getWorkingHoursStart = (workingHours?: WorkingHourInfo[]) => {
  if (!workingHours?.length) return;

  // Find the earliest start time from all working hours
  const earliestHour = workingHours.reduce((earliest, current) => {
    if (!earliest) return current.startTime;
    return earliest;
  }, workingHours[0]?.startTime);

  return earliestHour;
};

/**
 * Returns the latest end time (as a `'hh:mm A'` string) across a list of entries that each carry
 * a `startTime` string and a `duration` in minutes.  Returns `null` when the list is empty.
 *
 * Shared by `getWorkingHoursEnd` (legacy `WorkingHourInfo[]`) and
 * `scrollToPositionForCalendarGrid` (flat `workingHoursV3` events).
 */
const getLatestEndTime = (entries: { startTime: string; duration: number }[]): string | null => {
  let latestEnd: dayjs.Dayjs | null = null;
  for (const entry of entries) {
    const endTime = parseTimeString(entry.startTime).add(entry.duration, 'minute');
    if (!latestEnd || endTime.isAfter(latestEnd)) latestEnd = endTime;
  }
  return latestEnd ? latestEnd.format(TIME_FORMAT) : null;
};

const getWorkingHoursEnd = (workingHours?: WorkingHourInfo[]): string | null => {
  if (!workingHours?.length) return null;
  return getLatestEndTime(workingHours);
};

/**
 * Given an array of time strings (any format accepted by `parseTimeString`), returns the
 * chronologically earliest one, or `undefined` when the array is empty.
 *
 * Used to find the earliest working-hours start and the earliest appointment start inside
 * `scrollToPositionForCalendarGrid`.
 */
const getEarliestTimeString = (times: string[]): string | undefined =>
  times.reduce<string | undefined>((earliest, current) => {
    if (!earliest) return current;
    return parseTimeString(current).isBefore(parseTimeString(earliest)) ? current : earliest;
  }, undefined);

/**
 * Converts a 24-hour `'HH:mm'` time string to the `'hh:mm A'` display format used throughout
 * this file (`TIME_FORMAT`).
 */
const toDisplayTime = (hhMm: string): string => dayjs(hhMm, 'HH:mm').format(TIME_FORMAT);

const getEarliestAppointmentTime = (officeData: OfficeCalendarView) => {
  if (!officeData.columnEntities?.length) return;

  let earliestTime = '';

  for (const entity of officeData.columnEntities) {
    if (!entity.events?.length) continue;

    for (const event of entity.events) {
      if (event.appointmentType === 'APPOINTMENT' && event.startHour) {
        if (!earliestTime) {
          earliestTime = event.startHour;
        } else {
          const currentTime = parseTimeString(event.startHour);
          const earliest = parseTimeString(earliestTime);
          if (currentTime.isBefore(earliest)) {
            earliestTime = event.startHour;
          }
        }
      }
    }
  }

  return earliestTime;
};

const parseTimeString = (timeStr: string): dayjs.Dayjs => {
  let time = dayjs(timeStr, 'hh:mm A');
  if (!time.isValid()) time = dayjs(timeStr, 'HH:mm');
  if (!time.isValid()) time = dayjs(timeStr, 'h:mm A');
  return time;
};

const subtractMinutesFromTime = (timeStr: string, minutes: number): string => {
  const time = parseTimeString(timeStr);
  return time.subtract(minutes, 'minute').format(TIME_FORMAT);
};

const calculateScrollPositionForTime = (targetTime: string, pxPerMinute: number): number => {
  const time = parseTimeString(targetTime);

  if (!time.isValid()) {
    throw new Error(`Invalid time format: ${targetTime}`);
  }

  const targetHour = time.hour();
  const targetMinute = time.minute();
  const totalMinutesFromStart = targetHour * 60 + targetMinute;

  return totalMinutesFromStart * pxPerMinute;
};

const scrollTo = (containerElement: HTMLElement | null, scrollPosition: number): void => {
  if (!containerElement) return;

  containerElement.scrollTo({
    top: scrollPosition,
    behavior: 'smooth',
  });
};

const scrollToTime = (containerElement: HTMLElement | null, targetTime: string, pxPerMinute: number) => {
  if (!containerElement) return;

  try {
    const scrollPosition = calculateScrollPositionForTime(targetTime, pxPerMinute);
    scrollTo(containerElement, scrollPosition);
  } catch (error) {
    console.error('Failed to scroll to time:', error);
  }
};

const scrollToCenteredElement = (
  containerElement: HTMLElement,
  element: HTMLElement,
  officeEnd: string | null,
  pxPerMinute: number
): void => {
  const elRect = element.getBoundingClientRect();
  const containerRect = containerElement.getBoundingClientRect();
  // Calculate element's position relative to the scrollable container
  const elRelativeTop = elRect.top - containerRect.top + containerElement.scrollTop;
  // Calculate center position: element position - half of container height
  const containerHeight = containerElement.clientHeight;
  const centerPosition = elRelativeTop - containerHeight / 2;

  if (officeEnd) {
    const officeEndPlusBuffer = parseTimeString(officeEnd)
      .add(CALENDAR_SCROLL_TO_OFFSET_IN_MINUTES, 'minute')
      .format(TIME_FORMAT);
    const officeEndScrollPosition = calculateScrollPositionForTime(officeEndPlusBuffer, pxPerMinute);
    const maxAllowedScroll = officeEndScrollPosition - containerHeight;
    scrollTo(containerElement, Math.min(centerPosition, maxAllowedScroll));
  } else {
    scrollTo(containerElement, centerPosition);
  }
};

/**
 * Shared scroll logic for both scrollToPosition and scrollToPositionForCalendarGrid.
 * Today: centres on the current-time line (or startTime − 15 min if before office open).
 * Other days: scrolls to startTime − CALENDAR_SCROLL_TO_OFFSET_IN_MINUTES.
 */
const scrollBasedOnCurrentTime = (
  containerElement: HTMLElement,
  currentTime: string,
  startTime: string,
  officeEnd: string | null,
  pxPerMinute: number,
  isTodayDateSelected: boolean,
  currentTimeElement: HTMLElement | null
): void => {
  if (isTodayDateSelected) {
    const currentTimeParsed = parseTimeString(currentTime);
    const startTimeParsed = parseTimeString(startTime);

    if (currentTimeParsed.isBefore(startTimeParsed)) {
      scrollToTime(containerElement, subtractMinutesFromTime(startTime, 15), pxPerMinute);
    } else {
      if (currentTimeElement) {
        scrollToCenteredElement(containerElement, currentTimeElement, officeEnd, pxPerMinute);
      } else {
        scrollToTime(containerElement, DEFAULT_CALENDAR_SCROLL_TO_TIME, pxPerMinute);
      }
    }
  } else {
    scrollToTime(
      containerElement,
      subtractMinutesFromTime(startTime, CALENDAR_SCROLL_TO_OFFSET_IN_MINUTES),
      pxPerMinute
    );
  }
};

export const scrollToPosition = (
  containerElement: HTMLElement | null,
  calendarData: CalendarViewOffices,
  pxPerMinute: number
): void => {
  if (!containerElement || !calendarData?.length) return;

  try {
    // Consider only the first location's timezone for scrolling logic, the first location on the calendar is in the same order as the data array
    const firstOffice = calendarData[0];
    const officeTimezone = firstOffice?.officeTimezone;
    const workingHours = firstOffice.workingHours ?? [];
    const officeStart = getWorkingHoursStart(workingHours); // hh:mm A
    const officeEnd = getWorkingHoursEnd(workingHours); // hh:mm A
    const currentTimeObj = officeTimezone ? dayjs().tz(officeTimezone) : dayjs().tz(); // TODO: Update this for v2
    const currentTime = currentTimeObj.format(TIME_FORMAT);
    const isTodayDateSelected = dayjs(firstOffice.calendarDateValue).isSame(currentTimeObj, 'day');
    const startTime = officeStart || getEarliestAppointmentTime(firstOffice) || DEFAULT_CALENDAR_SCROLL_TO_TIME;
    const currentTimeElement = document.getElementById('current-time-progress-line');

    scrollBasedOnCurrentTime(
      containerElement,
      currentTime,
      startTime,
      officeEnd,
      pxPerMinute,
      isTodayDateSelected,
      currentTimeElement
    );
  } catch (error) {
    console.error('Failed to execute calendar scroll:', error);
  }
};

/**
 * Scrolls the calendar grid to the appropriate position on load.
 * Mirrors scrollToPosition: uses earliest workingHoursV3 start only if ≥ DEFAULT_CALENDAR_SCROLL_TO_TIME,
 * otherwise falls back to earliest appointment, then DEFAULT_CALENDAR_SCROLL_TO_TIME.
 */
export const scrollToPositionForCalendarGrid = (
  containerElement: HTMLElement | null,
  events: { eventType: string; startTime: string; duration?: number }[],
  pxPerMinute: number,
  calendarDate: string,
  timezone?: string
): void => {
  if (!containerElement) return;

  try {
    const currentTimeObj = timezone ? dayjs().tz(timezone) : dayjs().tz();
    const currentTime = currentTimeObj.format(TIME_FORMAT);
    const isTodayDateSelected = dayjs(calendarDate, 'MM/DD/YYYY').isSame(currentTimeObj, 'day');

    // Derive earliest working-hours start from workingHoursV3 events (format: 'HH:mm')
    // Filter out '00:00'
    const earliestWorkingHoursStart = getEarliestTimeString(
      events
        .filter((e) => e.eventType === 'workingHoursV3' && e.startTime && e.startTime !== '00:00')
        .map((e) => e.startTime)
    );

    // Derive working-hours end: max(startTime + duration) across all workingHoursV3 events
    // Filter out '00:00'
    const workingHoursEnd = getLatestEndTime(
      events.filter(
        (e): e is { eventType: string; startTime: string; duration: number } =>
          e.eventType === 'workingHoursV3' && !!e.startTime && e.startTime !== '00:00' && e.duration !== undefined
      )
    );

    // Derive earliest appointment start as a fallback (format: 'HH:mm')
    const earliestAppointmentStart = getEarliestTimeString(
      events.filter((e) => e.eventType === 'appointment' && e.startTime).map((e) => e.startTime)
    );

    const workingHoursStartDisplay = earliestWorkingHoursStart ? toDisplayTime(earliestWorkingHoursStart) : undefined;

    const validWorkingHoursStart =
      workingHoursStartDisplay &&
      !parseTimeString(workingHoursStartDisplay).isBefore(parseTimeString(DEFAULT_CALENDAR_SCROLL_TO_TIME))
        ? workingHoursStartDisplay
        : undefined;

    const resolvedStart =
      validWorkingHoursStart ||
      (earliestAppointmentStart ? toDisplayTime(earliestAppointmentStart) : undefined) ||
      DEFAULT_CALENDAR_SCROLL_TO_TIME;

    const startTime = resolvedStart;

    const currentTimeElement = containerElement.querySelector('[data-time-indicator-line]') as HTMLElement | null;

    scrollBasedOnCurrentTime(
      containerElement,
      currentTime,
      startTime,
      workingHoursEnd,
      pxPerMinute,
      isTodayDateSelected,
      currentTimeElement
    );
  } catch (error) {
    console.error('Failed to execute calendar grid scroll:', error);
  }
};

export const handleWaitlistAppointmentNotificationClick = (_payload: WaitlistAlert) => {
  dispatchTopBarTraySelectionEvent('quickfill');
};

export const getScheduleRequestEventId = (requestId: string | undefined, openingIndex: number) => {
  return requestId && `${requestId}-${openingIndex}`;
};
